import React, { useEffect, useState } from "react";
import config from "../../../config";
import { ChangeData } from "../../../utils/Change/changeTypes";
import ApiDropdown from "../../DropDown/Apidropdown";
import "../Incident/ForwardModal.css";

import { useNavigate } from "react-router-dom";
import { authFetch } from "../../../utils/Security/authFetch";

type Message = {
  id: number;
  username: string;
  message: string;
  date: string;
};

type Role = {
  roleId: number;
  roleName: string;
};

type Person = {
  id: number;
  name: string;
};

type ForwardModalProps = {
  isOpen: boolean;
  data: ChangeData | null;
  onClose: () => void;
};

const ForwardModal: React.FC<ForwardModalProps> = ({
  isOpen,
  data,
  onClose,
}) => {
  const navigate = useNavigate();

  const [journalMessages, setJournalMessages] = useState<Message[]>([]);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [userIdFromEmail, setUserIdFromEmail] = useState<number | null>(null);
  const [persons, setPersons] = useState<Person[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [selectedPersonEmail, setSelectedPersonEmail] = useState<string[]>([]);
  const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null);
  const [selectedPersonName, setSelectedPersonName] = useState<string | null>(
    null
  );
  const [originalResponsible, setOriginalResponsible] = useState<string | null>(
    null
  );

  const storedUsername = localStorage.getItem("username");

  const postMessage = async (payload: any) => {
    return authFetch(`${config.apiBaseUrl}/api/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  };
  const [senderFullName, setSenderFullName] = useState<string | null>(null);

  const fetchFullNameByUsername = async (username: string) => {
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/users/fullname?username=${encodeURIComponent(
          username
        )}`
      );

      if (res.ok) {
        const fullName = await res.text(); // endpoint returns String
        return fullName?.trim() || null;
      }

      return null;
    } catch (e) {
      console.error("Failed to fetch full name:", e);
      return null;
    }
  };
  useEffect(() => {
    if (isOpen && data?.ticketId) {
      fetchMessages();
      setPersons([]);
      setSelectedRole(null);
      setSelectedPersonId(null);
      setOriginalResponsible(data?.responsiblePerson ?? "Unknown");
      // ✅ fetch full name of logged in user
      const u = localStorage.getItem("username");
      if (u) {
        fetchFullNameByUsername(u).then((name) => setSenderFullName(name));
      } else {
        setSenderFullName(null);
      }
    }
  }, [isOpen]);

  const fetchMessages = async () => {
    try {
      const response = await authFetch(
        `${config.apiBaseUrl}/api/messages/ticket/${data?.ticketId}`
      );
      if (response.ok) {
        const messages: Message[] = await response.json();
        setJournalMessages(messages);
      } else {
        console.error("Failed to fetch messages");
      }
    } catch (error) {
      console.error("Error while fetching messages:", error);
    }
  };

  const handleRoleChange = async (roleId: number) => {
    setSelectedRole(roleId);

    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/persons/role/${roleId}`
      );
      if (res.ok) {
        const data: Person[] = await res.json();
        setPersons(data);
      }

      const roleRes = await authFetch(
        `${config.apiBaseUrl}/api/roles/${roleId}`
      );
      if (roleRes.ok) {
        const roleData: Role = await roleRes.json();
        setSelectedRoleName(roleData.roleName);
      } else {
        setSelectedRoleName(null);
      }
    } catch (err) {
      console.error("Error:", err);
      setPersons([]);
      setSelectedRoleName(null);
    }
  };

  const handlePersonChange = async (personId: number) => {
    setSelectedPersonId(personId);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/persons/${personId}`
      );
      if (res.ok) {
        const person: Person & { email?: string } = await res.json();
        const email = person.email;
        setSelectedPersonName(person.name);
        if (email) {
          setSelectedPersonEmail([email]);
          console.log("Forwarding email to:", selectedPersonEmail); // <-- Add this line
        }
        if (email) {
          const idRes = await authFetch(
            `${
              config.apiBaseUrl
            }/api/users/id-by-email?email=${encodeURIComponent(email)}`
          );
          if (idRes.ok) {
            const userId: number = await idRes.json();
            setUserIdFromEmail(userId);
          } else {
            console.error("Failed to get user ID from email");
            setUserIdFromEmail(null);
          }
        }
      } else {
        console.error("Failed to fetch person details");
        setUserIdFromEmail(null);
      }
    } catch (err) {
      console.error("Error fetching person/email/user ID:", err);
      setUserIdFromEmail(null);
    }
  };

  const handleForward = async () => {
    if (!data) {
      alert("Change data is missing.");
      return;
    }

    if (
      !data.ticketId ||
      userIdFromEmail === null ||
      !selectedRoleName ||
      !selectedPersonName ||
      !selectedRole
    ) {
      alert("Please select a person to forward the change to.");
      return;
    }

    const username = storedUsername || "System";
    const payload = {
      username,
      message: `Change With ID ${data.ticketId} forwarded from ${storedUsername} to ${selectedPersonName}.`,
      date: new Date().toISOString(),
      read: false,
      ticketId: data.ticketId,
    };

    try {
      const response = await authFetch(
        `${config.apiBaseUrl}/api/changes/${data.ticketId}/duplicate/${userIdFromEmail}`,
        { method: "POST" }
      );

      if (!response.ok) {
        console.error("Failed to forward change (duplicate)");
        alert("Failed to forward the change.");
        return;
      }

      const forwardResponse = await authFetch(
        `${config.apiBaseUrl}/api/changes/forward?ticketId=${encodeURIComponent(
          data.ticketId
        )}&role=${encodeURIComponent(
          selectedRoleName
        )}&person=${encodeURIComponent(selectedPersonName)}`,
        { method: "POST" }
      );

      if (forwardResponse.ok) {
        alert("Change forwarded successfully!");
        alert(selectedPersonEmail);
        if (selectedPersonEmail.includes("p.horkos@bcomsat.com")) {
          const email = [
            "p.horkos@bcomsat.com",
            "m.matta@bcomsat.com",
            "s.chemaly@bcomsat.com",
          ];

          try {
            await authFetch(`${config.apiBaseUrl}/api/email/send`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                to: selectedPersonEmail,
                subject: ` Change ID ${data.ticketId} | ${data.affectedCustomer} | forwarded to you.`,
                body: `
                  <div style="font-family: Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.6;">
                    <p>Hello,</p><br>
                    <p>This Ticket was forwarded to you with the following comment:</p><br><br>
                                            <strong>From:</strong> ${
                                              senderFullName ??
                                              storedUsername ??
                                              "System"
                                            }<br><br>
                    <strong>To:</strong> ${selectedPersonName}</p><br><br>
                    <strong>Customer:</strong> ${
                      data.affectedCustomer ?? ""
                    }<br><br>
     <strong>Network:</strong> ${data.affectedNetwork ?? ""}<br><br>
                    <strong>Site:</strong> ${data.affectedSite ?? ""}<br><br>
                    <strong>Link:</strong> ${data.affectedLink ?? ""}<br><br>
              <strong>Planned Start Date/Time:</strong> ${
                data.changeStartDatetime
                  ? data.changeStartDatetime.replace("T", " ")
                  : ""
              }<br><br>
                   <strong>Planned Start Date/Time:</strong> ${
                     data.changeCloseDatetime
                       ? data.changeCloseDatetime.replace("T", " ")
                       : ""
                   }<br><br>
                    <strong>Summary:</strong> ${data.summary ?? ""}<br><br>
                    <strong>Description:</strong> ${
                      data.description ?? ""
                    }<br><br>
                    <strong>Error Type:</strong> ${data.errorType ?? ""}<br><br>
                    <strong>Closure Code:</strong> ${
                      data.closureCode ?? ""
                    }<br><br>
                    <strong>Solution:</strong> ${data.solution ?? ""}</p><br>
                          <p>
        <a href=http://149.202.42.77:3000 target="_blank" style="color: #ffa500; font-weight: bold; text-decoration: none;">
          👉 View Change Details
        </a>
      </p>
                    <p>Regards,</p>
                     <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 20px;">
        <tr>
          <td style="vertical-align: top; padding-right: 12px;">
            <img src="cid:seslogo" alt="SES Logo" width="80" style="display: block;">
          </td>

          <td>
            <div style="padding: 12px; border-left: 4px solid #ffa500; background-color: #f9f9f9; color: #555; font-size: 13px;">
              <strong>Managed Services Operations</strong><br>
              Value-Added Service<br>
              Tel. & WhatsApp msg: +41 225521085<br>
              Email: <a href=mailto:support@bcomintelsat.com style="color: #555;">support@bcomintelsat.com</a>
            </div>
          </td>
        </tr>
      </table>
                  </div>
                `,
              }),
            });

            //console.log("Email sent successfully!");
          } catch (emailErr) {
            console.error("Failed to send email notification:", emailErr);
          }
        }
        if (selectedPersonEmail) {
          try {
            await authFetch(`${config.apiBaseUrl}/api/email/send`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                to: selectedPersonEmail,
                subject: `A change with ID ${data.ticketId} has been forwarded to you.`,
                body: `
                  <div style="font-family: Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.6;">
                    <p>Hello,</p>
                    <p>A Ticket with id ${
                      data.ticketId
                    } was forwarded to you with the following comments:</p>
                                           <strong>From:</strong> ${
                                             senderFullName ??
                                             storedUsername ??
                                             "System"
                                           }<br><br>
                    <strong>To:</strong> ${selectedPersonName}</p><br><br>
                    <strong>Customer:</strong> ${
                      data.affectedCustomer ?? ""
                    }<br><br>
     <strong>Network:</strong> ${data.affectedNetwork ?? ""}<br><br>
                    <strong>Site:</strong> ${data.affectedSite ?? ""}<br><br>
                    <strong>Link:</strong> ${data.affectedLink ?? ""}<br><br>
 <strong>Planned Start Date/Time:</strong> ${
   data.changeStartDatetime ? data.changeStartDatetime.replace("T", " ") : ""
 }<br><br>
                   <strong>Planned Start Date/Time:</strong> ${
                     data.changeCloseDatetime
                       ? data.changeCloseDatetime.replace("T", " ")
                       : ""
                   }<br><br>
                    <strong>Summary:</strong> ${data.summary ?? ""}<br><br>
                    <strong>Description:</strong> ${
                      data.description ?? ""
                    }<br><br>
                    <strong>Error Type:</strong> ${data.errorType ?? ""}<br><br>
                    <strong>Closure Code:</strong> ${
                      data.closureCode ?? ""
                    }<br><br>
                    <strong>Solution:</strong> ${data.solution ?? ""}</p><br>
                       <p>Regards,</p>
                          <p>
                          
        <a href=https://bdm.bcomsat.com target="_blank" style="color: #ffa500; font-weight: bold; text-decoration: none;">
          👉 View Change Details
        </a>
      </p>
                 
                     <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 20px;">
        <tr>
          <td style="vertical-align: top; padding-right: 12px;">
            <img src="cid:seslogo" alt="SES Logo" width="80" style="display: block;">
          </td>

          <td>
            <div style="padding: 12px; border-left: 4px solid #ffa500; background-color: #f9f9f9; color: #555; font-size: 13px;">
              <strong>Managed Services Operations</strong><br>
              Value-Added Service<br>
              Tel. & WhatsApp msg: +41 225521085<br>
              Email: <a href=mailto:support@bcomintelsat.com style="color: #555;">support@bcomintelsat.com</a>
            </div>
          </td>
        </tr>
      </table>
                  </div>
                `,
              }),
            });

            //console.log("Email sent successfully!");
          } catch (emailErr) {
            console.error("Failed to send email notification:", emailErr);
          }
        }

        onClose();

        const res = await postMessage(payload);
        if (res.ok) fetchMessages();
        else console.error("Failed to save message");

        setTimeout(() => {
          window.location.reload();
        }, 1500);

        navigate(`/change`);
      } else {
        console.error("Failed to call forwardChange endpoint");
        alert(
          "Change forwarded (duplicate), but failed to update responsible person/team."
        );
      }
    } catch (error) {
      console.error("Error forwarding change:", error);
      alert("An error occurred while forwarding the change.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="forward-modal-overlay">
      <div className="forward-modal-content">
        <div className="forward-modal-header">
          <h2 style={{ color: "white" }}>Forward Change</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="forward-modal-body">
          <p>
            Forwarding change with ID: <strong>{data?.ticketId}</strong>
          </p>

          <ApiDropdown<Role>
            api="roles"
            labelKey="roleName"
            valueKey="roleId"
            label="Select Role"
            onChange={(val) => handleRoleChange(Number(val))}
          />

          <ApiDropdown<Person>
            api=""
            labelKey="name"
            valueKey="id"
            label="Select Person"
            onChange={(val) => handlePersonChange(Number(val))}
            value={selectedPersonId ?? undefined}
            options={persons}
          />
        </div>

        <div className="forward-modal-footer">
          <button
            className="forward-button"
            onClick={handleForward}
            disabled={!data}
          >
            Forward
          </button>
          <button className="action-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForwardModal;
